import { CancelToken } from "axios";
import {
    queryQualityHubPinHashesAsync,
    findTaskListAsync,
    findActionListForSourceAsync,
    queryMyTaskListsAsync,
    TaskStatusModel,
    createTaskListItemStatusAsync,
    deleteTaskListItemStatusAsync,
    queryResourceTagsAsync,
    updateTaskListItemStatusAsync,
    updateTaskListItemOverridesAsync,
    setTaskListGroupOverridesAsync,
    queryOperationalTargetsAsync,
    OperationalTargetModel,
    createOperationalTargetAsync,
    updateOperationalTargetAsync,
    deleteOperationalTargetAsync,
    queryTaskListsAsync,
} from "src/api/qualityHubApi";
import {
    CreateTaskStatusAction,
    DeleteTaskStatusAction,
    QualityHubActionTypes,
    TaskListCreatedAction,
    TaskListUpdatedAction,
    TaskListDeletedAction,
    QueryMyTaskListsAction,
    FindTaskListAction,
    FindActionListForSourceAction,
    TaskListItemCreatedAction,
    TaskListItemUpdatedAction,
    TaskListItemDeletedAction,
    TaskListGroupCreatedAction,
    TaskListGroupUpdatedAction,
    TaskListGroupDeletedAction,
    TaskListItemStatusCreatedAction,
    TaskListItemStatusUpdatedAction,
    TaskListItemStatusDeletedAction,
    QueryResourceTagsAction,
    ResourceTagCreatedAction,
    ResourceTagUpdatedAction,
    ResourceTagDeletedAction,
    UpdateTaskStatusAction,
    RemoveFromOfflineQueueAction,
    UpdateTaskListItemOverridesAction,
    SetTaskListGroupOverridesAction,
    QueryOperationalTargetsAction,
    CreateOperationalTargetAction,
    DeleteOperationalTargetAction,
    UpdateOperationalTargetAction,
    OperationalTargetCreatedAction,
    OperationalTargetUpdatedAction,
    OperationalTargetDeletedAction,
    QueryTaskListsAction,
    QueryPinHashesAction,
    QueueSignatureStatusChangeAction,
    RemoveSignatureStatusChangeAction,
    SubmitSignatureQueueFulfilledAction,
    SubmitSignatureQueuePendingAction,
    SubmitSignatureQueueRejectedAction,
    SignatureQueueOperation,
} from "../types";
import { hashQualityHubPinCodeAsync, uniqueId } from "src/utils";
import { Dispatch } from "redux";
import { localize } from "src/l10n";

/**
 * An action that indicates a task list has been created.
 *
 * @param {Spintr.TaskList} item - The task list that was created.
 * @returns {TaskListCreatedAction} The action object.
 */
export function taskListCreated(list: Spintr.TaskList): TaskListCreatedAction {
    return {
        type: QualityHubActionTypes.TaskListCreated,
        list,
    };
}

/**
 * An action that indicates a task list has been updated.
 *
 * @param {Spintr.TaskList} item - The task list that was updated.
 * @returns {TaskListUpdatedAction} The action object.
 */
export function taskListUpdated(list: Spintr.TaskList): TaskListUpdatedAction {
    return {
        type: QualityHubActionTypes.TaskListUpdated,
        list,
    };
}

/**
 * An action that indicates a task list has been deleted.
 *
 * @param {number} id - The ID of the task list that was deleted.
 * @returns {TaskListDeletedAction} The action object.
 */
export function taskListDeleted(id: number): TaskListDeletedAction {
    return {
        type: QualityHubActionTypes.TaskListDeleted,
        id,
    };
}

/**
 * An action that indicates a task list group has been created.
 *
 * @param {Spintr.TaskListGroup} item - The task list group that was created.
 * @returns {TaskListGroupCreatedAction} The action object.
 */
export function taskListGroupCreated(
    group: Spintr.TaskListGroup,
): TaskListGroupCreatedAction {
    return {
        type: QualityHubActionTypes.TaskListGroupCreated,
        group,
    };
}

/**
 * An action that indicates a task list group has been updated.
 *
 * @param {Spintr.TaskListGroup} item - The task list group that was updated.
 * @returns {TaskListGroupUpdatedAction} The action object.
 */
export function taskListGroupUpdated(
    group: Spintr.TaskListGroup,
): TaskListGroupUpdatedAction {
    return {
        type: QualityHubActionTypes.TaskListGroupUpdated,
        group,
    };
}

/**
 * An action that indicates a task list group has been deleted.
 *
 * @param {number} id - The ID of the task list group that was deleted.
 * @returns {TaskListGroupDeletedAction} The action object.
 */
export function taskListGroupDeleted(
    groupId: number,
    listId: number,
): TaskListGroupDeletedAction {
    return {
        type: QualityHubActionTypes.TaskListGroupDeleted,
        listId,
        groupId,
    };
}

/**
 * An action that indicates a task list item has been created.
 *
 * @param {Spintr.TaskListTask} item - The task list item that was created.
 * @returns {TaskListItemCreatedAction} The action object.
 */
export function taskListItemCreated(
    item: Spintr.TaskListTask,
): TaskListItemCreatedAction {
    return {
        type: QualityHubActionTypes.TaskListItemCreated,
        item,
    };
}

/**
 * An action that indicates a task list item has been updated.
 *
 * @param {Spintr.TaskListTask} item - The task list item that was updated.
 * @returns {TaskListItemUpdatedAction} The action object.
 */
export function taskListItemUpdated(
    item: Spintr.TaskListTask,
): TaskListItemUpdatedAction {
    return {
        type: QualityHubActionTypes.TaskListItemUpdated,
        item,
    };
}

/**
 * An action that indicates a task list item has been deleted.
 *
 * @param {number} id - The ID of the task list item that was deleted.
 * @returns {TaskListItemDeletedAction} The action object.
 */
export function taskListItemDeleted(
    id: number,
    listId: number,
): TaskListItemDeletedAction {
    return {
        type: QualityHubActionTypes.TaskListItemDeleted,
        itemId: id,
        listId,
    };
}

/**
 * An action that indicates a task list item status has been created.
 *
 * @param {Spintr.TaskListItemStatus} status - The task list item status that was created.
 * @param {number} listId - The ID of the task list.
 * @returns {TaskListItemStatusCreatedAction} The action object.
 */
export function taskListItemStatusCreated(
    status: Spintr.TaskListItemStatus,
    listId: number,
): TaskListItemStatusCreatedAction {
    return {
        type: QualityHubActionTypes.TaskListItemStatusCreated,
        status,
        listId,
    };
}

/**
 * An action that indicates a task list item status has been updated.
 *
 * @param {Spintr.TaskListItemStatus} status - The task list item status that was updated.
 * @param {number} listId - The ID of the task list.
 * @returns {TaskListItemStatusUpdatedAction} The action object.
 */
export function taskListItemStatusUpdated(
    status: Spintr.TaskListItemStatus,
    listId: number,
): TaskListItemStatusUpdatedAction {
    return {
        type: QualityHubActionTypes.TaskListItemStatusUpdated,
        status,
        listId,
    };
}

/**
 * An action that indicates a task list item status has been deleted.
 *
 * @param {number} statusId - The ID of the task list item status that was deleted.
 * @param {number} listId - The ID of the task list.
 * @returns {TaskListItemStatusDeletedAction} The action object.
 */
export function taskListItemStatusDeleted(
    statusId: number,
    listId: number,
): TaskListItemStatusDeletedAction {
    return {
        type: QualityHubActionTypes.TaskListItemStatusDeleted,
        statusId,
        listId,
    };
}

/**
 * An action that signals the creation of a new resource tag.
 *
 * @param {Spintr.ResourceTag} tag - The resource tag that was created.
 * @returns {ResourceTagCreatedAction} The action object.
 */
export function resourceTagCreated(
    tag: Spintr.ResourceTag,
): ResourceTagCreatedAction {
    return {
        type: QualityHubActionTypes.ResourceTagCreated,
        tag,
    };
}

/**
 * An action that signals the update of an existing resource tag.
 *
 * @param {Spintr.ResourceTag} tag - The resource tag that was updated.
 * @returns {ResourceTagUpdatedAction} The action object.
 */
export function resourceTagUpdated(
    tag: Spintr.ResourceTag,
): ResourceTagUpdatedAction {
    return {
        type: QualityHubActionTypes.ResourceTagUpdated,
        tag,
    };
}

/**
 * An action that signals the deletion of a resource tag.
 *
 * @param {number} tagId - The ID of the resource tag that was deleted.
 * @returns {ResourceTagDeletedAction} The action object.
 */
export function resourceTagDeleted(
    tagId: number,
): ResourceTagDeletedAction {
    return {
        type: QualityHubActionTypes.ResourceTagDeleted,
        tagId,
    };
}

/**
 * A thunk action that queries all task lists.
 *
 * @param {CancelToken} [cancelToken] - An optional cancel token for the request.
 * @returns {QueryTaskListsAction} The action object.
 */
export function queryTaskLists(
    cancelToken?: CancelToken | undefined,
): QueryTaskListsAction {
    return {
        payload: queryTaskListsAsync(cancelToken),
        type: QualityHubActionTypes.QueryTaskLists,
        meta: {},
    };
}

/**
 * A thunk action that queries the user's task lists.
 *
 * @param {number} [nodeId] - An optional Quality Hub node ID to load task lists for.
 * @param {CancelToken} [cancelToken] - An optional cancel token for the request.
 * @returns {QueryMyTaskListsAction} The action object.
 */
export function queryMyTaskLists(
    nodeId?: number | undefined,
    asOf?: Date | undefined,
    cancelToken?: CancelToken | undefined
): QueryMyTaskListsAction {
    return {
        payload: queryMyTaskListsAsync({
            asOf,
            nodeId,
        }, cancelToken),
        type: QualityHubActionTypes.QueryMyTaskLists,
        meta: {
            nodeId,
            asOf,
        },
    };
}

/**
 * A thunk action that finds a task list by its ID.
 *
 * @param {number} id - The ID of the task list to find.
 * @param {CancelToken} [cancelToken] - An optional cancel token for the request.
 * @returns {FindTaskListAction} The action object.
 */
export function findTaskList(
    id: number,
    cancelToken?: CancelToken | undefined
): FindTaskListAction {
    return {
        payload: findTaskListAsync(id, cancelToken),
        type: QualityHubActionTypes.FindTaskList,
        meta: { id },
    };
}

/**
 * A thunk action that resolves the action list a follow-up action created
 * from the given source list would land in. Eagerly creates the action
 * list server-side if one does not yet exist.
 *
 * @param {number} sourceTaskListId - The ID of the source task list.
 * @param {number} [nodeId] - Optional override for the owning node.
 * @param {CancelToken} [cancelToken] - An optional cancel token.
 * @returns {FindActionListForSourceAction} The action object.
 */
export function findActionListForSource(
    sourceTaskListId: number,
    nodeId?: number | undefined,
    cancelToken?: CancelToken | undefined
): FindActionListForSourceAction {
    return {
        payload: findActionListForSourceAsync(sourceTaskListId, nodeId, cancelToken),
        type: QualityHubActionTypes.FindActionListForSource,
        meta: { sourceTaskListId, nodeId },
    };
}

export function createTaskStatus(
    model: TaskStatusModel,
    listId: number,
    cancelToken?: CancelToken | undefined,
): CreateTaskStatusAction {
    return {
        type: QualityHubActionTypes.CreateTaskStatus,
        payload: createTaskListItemStatusAsync(model, cancelToken),
        meta: {
            listId,
            model
        },
    };
}

export function updateTaskStatus(
    model: TaskStatusModel,
    status: Spintr.TaskListItemStatus,
    listId: number,
    cancelToken?: CancelToken | undefined,
): UpdateTaskStatusAction {
    return {
        type: QualityHubActionTypes.UpdateTaskStatus,
        payload: updateTaskListItemStatusAsync(status.id, model, cancelToken),
        meta: {
            listId,
            status,
            model,
        },
    };
}

export function deleteTaskStatus(
    status: Spintr.TaskListItemStatus,
    listId: number,
    cancelToken?: CancelToken | undefined,
): DeleteTaskStatusAction {
    return {
        type: QualityHubActionTypes.DeleteTaskStatus,
        payload: deleteTaskListItemStatusAsync(status.id, cancelToken),
        meta: {
            listId,
            status
        },
    };
}

export function queryResourceTags(
    skip: number,
    take: number,
    search?: string,
    cancelToken?: CancelToken | undefined
): QueryResourceTagsAction {
    return {
        payload: queryResourceTagsAsync(skip, take, search, cancelToken),
        type: QualityHubActionTypes.QueryResourceTags,
    };
}

export function queryQualityHubPinHashes(
    cancelToken?: CancelToken | undefined,
): QueryPinHashesAction {
    return {
        payload: queryQualityHubPinHashesAsync(cancelToken),
        type: QualityHubActionTypes.QueryPinHashes,
    };
}

/**
 * Creates an action for the removal of an operation from the Quality Hub
 * offline queue.
 *
 * @param {string} id - The ID of the operation to remove from the queue.
 * @return {RemoveFromOfflineQueueAction} The action object.
 */
export function removeFromQualityHubOfflineQueue(
    id: string,
): RemoveFromOfflineQueueAction {
    return {
        type: QualityHubActionTypes.RemoveFromOfflineQueue,
        operationId: id,
    };
}

export function queueSignatureStatusChange(
    operation: Omit<SignatureQueueOperation, "id">,
): QueueSignatureStatusChangeAction {
    return {
        type: QualityHubActionTypes.QueueSignatureStatusChange,
        operation: {
            ...operation,
            id: uniqueId("QH-SIGNATURE-OP_"),
        },
    };
}

export function removeSignatureStatusChange(
    id: string,
): RemoveSignatureStatusChangeAction {
    return {
        type: QualityHubActionTypes.RemoveSignatureStatusChange,
        operationId: id,
    };
}

function submitSignatureQueuePending(): SubmitSignatureQueuePendingAction {
    return {
        type: QualityHubActionTypes.SubmitSignatureQueuePending,
    };
}

function submitSignatureQueueFulfilled(): SubmitSignatureQueueFulfilledAction {
    return {
        type: QualityHubActionTypes.SubmitSignatureQueueFulfilled,
    };
}

function submitSignatureQueueRejected(
    errorMessage?: string,
): SubmitSignatureQueueRejectedAction {
    return {
        type: QualityHubActionTypes.SubmitSignatureQueueRejected,
        errorMessage,
    };
}

/**
 * Validates the entered PIN code and, if successful, dispatches create/update
 * status actions for all queued signature-required operations.
 *
 * When online, defers validation to the backend (authoritative) so a stale
 * cache can't lock users out. When offline, falls back to the cached hashes
 * loaded on Quality Hub mount.
 */
export function submitSignatureQueue(
    pinCode: string,
    reporterId: number,
) {
    return async (
        dispatch: Dispatch,
        getState: () => Spintr.AppState,
    ): Promise<boolean> => {
        dispatch(submitSignatureQueuePending());

        const isOnline = typeof navigator === "undefined" || navigator.onLine !== false;

        if (!isOnline) {
            const state = getState();
            if (state.qualityHub.pinHashes.length === 0) {
                dispatch(submitSignatureQueueRejected(localize("QH_NO_PIN_AVAILABLE")));
                return false;
            }

            const enteredHash = await hashQualityHubPinCodeAsync(reporterId, pinCode);
            const hashExists = state.qualityHub.pinHashes.some(
                (hash) => hash.toLowerCase() === enteredHash.toLowerCase(),
            );
            if (!hashExists) {
                dispatch(submitSignatureQueueRejected(localize("QH_INVALID_PIN")));
                return false;
            }
        }

        const queueItems = getState().qualityHub.signatureQueue.operations;

        try {
            for (const op of queueItems) {
                const model: TaskStatusModel = {
                    ...op.model,
                    pinCode,
                    reporterId,
                    signatureQueueItemId: op.id,
                };

                if (op.previousStatus) {
                    await (dispatch(updateTaskStatus(model, op.previousStatus, op.listId)) as unknown as Promise<void>);
                } else {
                    await (dispatch(createTaskStatus(model, op.listId)) as unknown as Promise<void>);
                }
            }
        } catch (err) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            if (status === 400) {
                // Refresh the offline cache so a subsequent attempt after a
                // server-side PIN rotation works without a full page reload.
                dispatch(queryQualityHubPinHashes() as any);
                dispatch(submitSignatureQueueRejected(localize("QH_INVALID_PIN")));
            } else {
                dispatch(submitSignatureQueueRejected(localize("TeknisktFelProvaIgenEllerKontaktaAdministrator")));
            }
            return false;
        }

        dispatch(submitSignatureQueueFulfilled());
        return true;
    };
}

export function setTaskListGroupOverrides(
    listId: number,
    overrides: Record<string, Partial<Spintr.TaskListGroup>>,
    cancelToken?: CancelToken,
): SetTaskListGroupOverridesAction {
    return {
        type: QualityHubActionTypes.SetTaskListGroupOverrides,
        payload: setTaskListGroupOverridesAsync(listId, overrides, cancelToken),
        meta: {
            listId,
            overrides,
        },
    };
}

/**
 * Creates an action to update task list item overrides.
 *
 * @param {number} listId - The ID of the task list.
 * @param {Record<string, Partial<Spintr.TaskListTask>>} overrides - The task overrides to apply.
 * @param {CancelToken} [cancelToken] - An optional cancel token for the request.
 * @return {UpdateTaskListItemOverridesAction} The action object.
 */
export function updateTaskListItemOverrides(
    listId: number,
    overrides: Record<string, Partial<Spintr.TaskListTask>>,
    cancelToken?: CancelToken,
): UpdateTaskListItemOverridesAction {
    return {
        type: QualityHubActionTypes.UpdateTaskListItemOverrides,
        payload: updateTaskListItemOverridesAsync(listId, overrides, cancelToken),
        meta: {
            listId,
            overrides,
        },
    };
}

export function queryOperationalTargets(
    signal?: AbortSignal,
): QueryOperationalTargetsAction {
    return {
        type: QualityHubActionTypes.QueryOperationalTargets,
        payload: queryOperationalTargetsAsync(signal),
    };
}

export function createOperationalTarget(
    model: OperationalTargetModel,
    cancelToken?: CancelToken,
): CreateOperationalTargetAction {
    return {
        type: QualityHubActionTypes.CreateOperationalTarget,
        payload: createOperationalTargetAsync(model, cancelToken),
        meta: { model },
    };
}

export function updateOperationalTarget(
    id: number,
    model: OperationalTargetModel,
    cancelToken?: CancelToken,
): UpdateOperationalTargetAction {
    return {
        type: QualityHubActionTypes.UpdateOperationalTarget,
        payload: updateOperationalTargetAsync(id, model, cancelToken),
        meta: { model, id, },
    };
}

export function deleteOperationalTarget(
    target: Spintr.OperationalTarget,
    cancelToken?: CancelToken,
): DeleteOperationalTargetAction {
    return {
        type: QualityHubActionTypes.DeleteOperationalTarget,
        payload: deleteOperationalTargetAsync(target.id, cancelToken),
        meta: { target },
    };
}

export function operationalTargetCreated(
    target: Spintr.OperationalTarget,
): OperationalTargetCreatedAction {
    return {
        type: QualityHubActionTypes.OperationalTargetCreated,
        target,
    };
}

export function operationalTargetUpdated(
    target: Spintr.OperationalTarget,
): OperationalTargetUpdatedAction {
    return {
        type: QualityHubActionTypes.OperationalTargetUpdated,
        target,
    };
}

export function operationalTargetDeleted(
    targetId: number,
): OperationalTargetDeletedAction {
    return {
        type: QualityHubActionTypes.OperationalTargetDeleted,
        targetId,
    };
}

